/**
 * Start Scribe — split control for the visit-note toolbar.
 *
 * The left segment starts the AI scribe; the caret segment holds secondary
 * scribe options and only renders when there are any. Presentational only — it owns the menu anchor and nothing
 * else, so the caller decides what starting the scribe means.
 */

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MicIcon from "@mui/icons-material/Mic";
import {
  ButtonBase,
  Divider,
  Menu,
  MenuItem,
  SxProps,
  Theme,
} from "@mui/material";
import { MouseEvent, useState } from "react";
import Flex from "@/components/Flex";
import Text from "@/components/Text";
import {
  CHART_COLORS,
  CHART_DIMENSIONS,
  CHART_TYPOGRAPHY,
} from "@/components/app/provider/patient/chart";
import { SelectItem } from "@/utils/utils";
import {
  START_SCRIBE_BUTTON,
  START_SCRIBE_MENU_ORIGIN,
} from "../contant/encounter-constant";

export interface StartScribeButtonProps {
  /** Primary action — fired by the labelled segment. */
  onStart: () => void;
  /** Secondary actions behind the caret; without any, the caret is not rendered. */
  options?: SelectItem[];
  onOptionClick?: (value: string) => void;
  sx?: SxProps<Theme>;
}

const StartScribeButton = ({
  onStart,
  options = [],
  onOptionClick,
  sx,
}: StartScribeButtonProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isMenuOpen = Boolean(anchorEl);
  const hasOptions = options.length > 0;

  const handleCaretClick = (event: MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);

  const closeMenu = () => setAnchorEl(null);

  const handleOptionClick = (value: string) => {
    closeMenu();
    onOptionClick?.(value);
  };

  const segmentSx: SxProps<Theme> = {
    height: "100%",
    "&:hover": { backgroundColor: START_SCRIBE_BUTTON.ACCENT_HOVER_BG },
  };

  return (
    <>
      <Flex
        alignItems="center"
        sx={{
          height: CHART_DIMENSIONS.controlHeight,
          border: `1px solid ${START_SCRIBE_BUTTON.ACCENT}`,
          borderRadius: START_SCRIBE_BUTTON.RADIUS,
          backgroundColor: CHART_COLORS.surfaceWhite,
          overflow: "hidden",
          ...sx,
        }}
      >
        <ButtonBase
          onClick={onStart}
          sx={{
            ...segmentSx,
            gap: START_SCRIBE_BUTTON.LABEL_GAP,
            px: START_SCRIBE_BUTTON.LABEL_PX,
          }}
        >
          <MicIcon
            sx={{
              color: START_SCRIBE_BUTTON.ACCENT,
              fontSize: CHART_DIMENSIONS.iconSm,
            }}
          />
          <Text
            isTabletext
            c={CHART_COLORS.textDark}
            fontWeight={CHART_TYPOGRAPHY.weight.medium}
          >
            {START_SCRIBE_BUTTON.LABEL}
          </Text>
        </ButtonBase>

        {hasOptions && (
          <>
            <Divider
              orientation="vertical"
              flexItem
              sx={{ borderColor: START_SCRIBE_BUTTON.ACCENT }}
            />

            <ButtonBase
              aria-label={START_SCRIBE_BUTTON.MENU_ARIA_LABEL}
              aria-haspopup="menu"
              aria-expanded={isMenuOpen}
              onClick={handleCaretClick}
              sx={{ ...segmentSx, width: START_SCRIBE_BUTTON.CARET_WIDTH }}
            >
              <KeyboardArrowDownIcon
                sx={{
                  color: CHART_COLORS.textDark,
                  fontSize: CHART_DIMENSIONS.iconSm,
                }}
              />
            </ButtonBase>
          </>
        )}
      </Flex>

      {hasOptions && (
        <Menu
          anchorEl={anchorEl}
          open={isMenuOpen}
          onClose={closeMenu}
          anchorOrigin={START_SCRIBE_MENU_ORIGIN.anchor}
          transformOrigin={START_SCRIBE_MENU_ORIGIN.transform}
        >
          {options.map((option) => (
            <MenuItem
              key={option.value}
              selected={option.selected}
              onClick={() => handleOptionClick(option.value)}
            >
              <Text isTabletext c={CHART_COLORS.textDark}>
                {option.label}
              </Text>
            </MenuItem>
          ))}
        </Menu>
      )}
    </>
  );
};

export default StartScribeButton;
